import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import orderBy from 'lodash/orderBy';

import { AVERAGE_HOURS_IN_MONTH } from '@cast/constants';
import type {
  ClusterOverviewItem,
  ClusterOverviewState,
  OrganizationOverviewAgentResourceMetrics,
  OrganizationOverviewCloudResourceMetrics,
  OrganizationOverviewData,
  OrganizationOverviewResourceTimeseriesPoint,
  OrganizationOverviewResponse,
  OrganizationOverviewSummary,
} from '@cast/types';
import {
  enhanceClusterSummary,
  type CostReportClusterSummaryNormalized,
} from '@cast/shared-cost-data-access';
import { AgentStatus, Cluster } from '@cast/types';
import { isClusterManaged } from '@cast/shared-cluster-context-data-access';
import { toBig, toNumOrZero } from '@cast/utils';

import { buildFleetMemoryMetrics } from './fleetMemoryMetrics';
import {
  resolveCpuUsed,
  resolveFleetResourceTotals,
  resolveRamUsed,
} from './overviewResourceMetrics';
import type { ResourceChartPoint, ResourceType } from './dashboardCharts';

dayjs.extend(utc);

type MergedOverviewData = {
  summary?: OrganizationOverviewSummary;
  resourceTimeseries: OrganizationOverviewResourceTimeseriesPoint[];
};

const sumNumericStrings = (
  left?: string,
  right?: string
): string | undefined => {
  if (left === undefined && right === undefined) {
    return undefined;
  }

  return String(toNumOrZero(left) + toNumOrZero(right));
};

const sumOptionalNumbers = (
  left?: number,
  right?: number
): number | undefined => {
  if (left === undefined && right === undefined) {
    return undefined;
  }

  return (left ?? 0) + (right ?? 0);
};

const mergeOrganizationSummaries = (
  left?: OrganizationOverviewSummary,
  right?: OrganizationOverviewSummary
): OrganizationOverviewSummary | undefined => {
  if (!left && !right) {
    return undefined;
  }

  if (!left) {
    return right;
  }

  if (!right) {
    return left;
  }

  const providerMap = new Map<
    string,
    { provider?: string; clusterCount?: number; costHourly?: string }
  >();

  [...(left.providers ?? []), ...(right.providers ?? [])].forEach((entry) => {
    const provider = entry.provider ?? 'unknown';
    const existing = providerMap.get(provider);

    providerMap.set(provider, {
      provider,
      clusterCount: sumOptionalNumbers(
        existing?.clusterCount,
        entry.clusterCount
      ),
      costHourly: sumNumericStrings(existing?.costHourly, entry.costHourly),
    });
  });

  return {
    totalClusters: sumOptionalNumbers(left.totalClusters, right.totalClusters),
    totalNodes: sumOptionalNumbers(left.totalNodes, right.totalNodes),
    avgCpuUtilization: sumNumericStrings(
      left.avgCpuUtilization,
      right.avgCpuUtilization
    ),
    avgRamUtilization: sumNumericStrings(
      left.avgRamUtilization,
      right.avgRamUtilization
    ),
    totalCostHourly: sumNumericStrings(
      left.totalCostHourly,
      right.totalCostHourly
    ),
    totalCpuProvisioned: sumNumericStrings(
      left.totalCpuProvisioned,
      right.totalCpuProvisioned
    ),
    totalRamProvisioned: sumNumericStrings(
      left.totalRamProvisioned,
      right.totalRamProvisioned
    ),
    totalGpuProvisioned: sumNumericStrings(
      left.totalGpuProvisioned,
      right.totalGpuProvisioned
    ),
    spotNodeCount: sumOptionalNumbers(left.spotNodeCount, right.spotNodeCount),
    onDemandNodeCount: sumOptionalNumbers(
      left.onDemandNodeCount,
      right.onDemandNodeCount
    ),
    spotCostHourly: sumNumericStrings(
      left.spotCostHourly,
      right.spotCostHourly
    ),
    onDemandCostHourly: sumNumericStrings(
      left.onDemandCostHourly,
      right.onDemandCostHourly
    ),
    rightsizedCostHourly: sumNumericStrings(
      left.rightsizedCostHourly,
      right.rightsizedCostHourly
    ),
    potentialSavingsHourly: sumNumericStrings(
      left.potentialSavingsHourly,
      right.potentialSavingsHourly
    ),
    providers: [...providerMap.values()],
    bySource: [...(left.bySource ?? []), ...(right.bySource ?? [])],
  };
};

const mergeResourceTimeseriesPoints = (
  left: OrganizationOverviewResourceTimeseriesPoint[],
  right: OrganizationOverviewResourceTimeseriesPoint[]
): OrganizationOverviewResourceTimeseriesPoint[] => {
  const byTimestamp = new Map<
    string,
    OrganizationOverviewResourceTimeseriesPoint
  >();

  [...left, ...right].forEach((point) => {
    if (!point.timestamp) {
      return;
    }

    const existing = byTimestamp.get(point.timestamp);

    byTimestamp.set(point.timestamp, {
      timestamp: point.timestamp,
      cpuProvisioned: sumNumericStrings(
        existing?.cpuProvisioned,
        point.cpuProvisioned
      ),
      cpuRequested: sumNumericStrings(
        existing?.cpuRequested,
        point.cpuRequested
      ),
      cpuUsed: sumNumericStrings(existing?.cpuUsed, point.cpuUsed),
      cpuUtilization: sumNumericStrings(
        existing?.cpuUtilization,
        point.cpuUtilization
      ),
      ramProvisionedGib: sumNumericStrings(
        existing?.ramProvisionedGib,
        point.ramProvisionedGib
      ),
      ramRequestedGib: sumNumericStrings(
        existing?.ramRequestedGib,
        point.ramRequestedGib
      ),
      ramUsedGib: sumNumericStrings(existing?.ramUsedGib, point.ramUsedGib),
      ramUtilization: sumNumericStrings(
        existing?.ramUtilization,
        point.ramUtilization
      ),
      storageProvisionedGib: sumNumericStrings(
        existing?.storageProvisionedGib,
        point.storageProvisionedGib
      ),
      storageRequestedGib: sumNumericStrings(
        existing?.storageRequestedGib,
        point.storageRequestedGib
      ),
      storageClaimedGib: sumNumericStrings(
        existing?.storageClaimedGib,
        point.storageClaimedGib
      ),
      costHourly: sumNumericStrings(existing?.costHourly, point.costHourly),
      optimalCostHourly: sumNumericStrings(
        existing?.optimalCostHourly,
        point.optimalCostHourly
      ),
    });
  });

  return orderBy([...byTimestamp.values()], 'timestamp');
};

export const mergeOverviewSections = (
  agent?: OrganizationOverviewData,
  agentless?: OrganizationOverviewData
): MergedOverviewData => ({
  summary: mergeOrganizationSummaries(agent?.summary, agentless?.summary),
  resourceTimeseries: mergeResourceTimeseriesPoints(
    agent?.resourceTimeseries ?? [],
    agentless?.resourceTimeseries ?? []
  ),
});

const getPrimarySourceMetrics = (
  item: ClusterOverviewItem
): {
  agentMetrics?: OrganizationOverviewAgentResourceMetrics;
  cloudMetrics?: OrganizationOverviewCloudResourceMetrics;
} => {
  const sourceEntry = item.sourceData?.find(
    (entry) => entry.info?.source === item.primarySource
  );

  return {
    agentMetrics: sourceEntry?.agentMetrics,
    cloudMetrics: sourceEntry?.cloudMetrics,
  };
};

const isDiscoveredOverviewCluster = (item: ClusterOverviewItem): boolean =>
  item.state === ('CLUSTER_STATE_DISCOVERED' as ClusterOverviewState);

export const mapOverviewClusterToSummaryNormalized = (
  item: ClusterOverviewItem
): ReturnType<typeof enhanceClusterSummary> => {
  const { agentMetrics, cloudMetrics } = getPrimarySourceMetrics(item);
  const zero = toBig(0);

  const cpuAllocatable = toBig(
    agentMetrics?.cpuProvisioned ??
      item.cpuProvisioned ??
      cloudMetrics?.cpuProvisioned
  );
  const cpuRequested = toBig(
    agentMetrics?.cpuRequested ?? item.cpuRequested ?? 0
  );
  const cpuUsed = toBig(resolveCpuUsed(item, agentMetrics, cloudMetrics));

  const ramAllocatable = toBig(
    agentMetrics?.ramProvisioned ??
      item.ramProvisioned ??
      cloudMetrics?.ramProvisioned
  );
  const ramRequested = toBig(
    agentMetrics?.ramRequested ?? item.ramRequested ?? 0
  );
  const ramUsed = toBig(resolveRamUsed(item, agentMetrics, cloudMetrics));

  const gpuAllocatable = toBig(
    agentMetrics?.gpuAllocatable ?? item.gpuProvisioned ?? 0
  );
  const gpuRequested = toBig(agentMetrics?.gpuRequested ?? 0);
  const gpuUsed = zero;

  const normalizedSummary: CostReportClusterSummaryNormalized = {
    clusterId: item.clusterId,
    clusterScore: '',
    nodesCountTotal: toBig(item.nodeCount),
    nodesCountManagedTotal: zero,
    nodeCountOnDemand: toBig(item.onDemandNodeCount),
    nodeCountSpot: toBig(item.spotNodeCount),
    nodeCountSpotFallbackCastai: zero,
    nodeCountOnDemandCastai: zero,
    nodeCountSpotCastai: zero,
    cpuAllocatableTotal: cpuAllocatable,
    cpuRequestedTotal: cpuRequested,
    cpuProvisionedTotal: cpuAllocatable,
    cpuCostHourlyTotal: toBig(item.costHourly),
    cpuUsed,
    ramAllocatableTotal: ramAllocatable,
    ramRequestedTotal: ramRequested,
    ramProvisionedTotal: ramAllocatable,
    ramUsed,
    gpuProvisionedTotal: gpuAllocatable,
    gpuAllocatableTotal: gpuAllocatable,
    gpuUsedTotal: gpuUsed,
    gpuRequestedTotal: gpuRequested,
    costHourlyOnDemand: toBig(item.onDemandCostHourly),
    costHourlySpot: toBig(item.spotCostHourly),
    costHourlySpotFallback: zero,
    cpuAllocatableOnDemand: cpuAllocatable,
    cpuAllocatableSpot: zero,
    cpuAllocatableSpotFallback: zero,
    cpuRequestedOnDemand: cpuRequested,
    cpuRequestedSpot: zero,
    cpuRequestedSpotFallback: zero,
    cpuProvisionedOnDemand: cpuAllocatable,
    cpuProvisionedSpot: zero,
    cpuProvisionedSpotFallback: zero,
    ramAllocatableOnDemand: ramAllocatable,
    ramAllocatableSpot: zero,
    ramAllocatableSpotFallback: zero,
    ramRequestedOnDemand: ramRequested,
    ramRequestedSpot: zero,
    ramRequestedSpotFallback: zero,
    ramProvisionedOnDemand: ramAllocatable,
    ramProvisionedSpot: zero,
    ramProvisionedSpotFallback: zero,
    gpuProvisionedOnDemand: gpuAllocatable,
    gpuProvisionedSpot: zero,
    gpuProvisionedSpotFallback: zero,
    gpuAllocatableOnDemand: gpuAllocatable,
    gpuAllocatableSpot: zero,
    gpuAllocatableSpotFallback: zero,
    gpuRequestedOnDemand: gpuRequested,
    gpuRequestedSpot: zero,
    gpuRequestedSpotFallback: zero,
    gpuUsedOnDemand: gpuUsed,
    gpuUsedSpot: zero,
    gpuUsedSpotFallback: zero,
    gpuIdleOnDemand: zero,
    gpuIdleSpot: zero,
    gpuIdleSpotFallback: zero,
    gpuNotUsedOnDemand: zero,
    gpuNotUsedSpot: zero,
    gpuNotUsedSpotFallback: zero,
    cpuCostHourlyOnDemand: zero,
    cpuCostHourlySpot: zero,
    cpuCostHourlySpotFallback: zero,
    ramCostHourlyOnDemand: zero,
    ramCostHourlySpot: zero,
    ramCostHourlySpotFallback: zero,
    gpuCostHourlyOnDemand: zero,
    gpuCostHourlySpot: zero,
    gpuCostHourlySpotFallback: zero,
    podCount: toBig(item.podCount),
    unschedulablePodCount: zero,
    unknownNodeCount: zero,
    scheduledPodsCount: zero,
    storageProvisioned: toBig(agentMetrics?.storageProvisioned ?? 0),
    storageRequested: toBig(agentMetrics?.storageRequested ?? 0),
    storageClaimed: toBig(agentMetrics?.storageClaimed ?? 0),
    storageCostHourly: zero,
    tpuAllocatableOnDemand: zero,
    tpuAllocatableSpot: zero,
    tpuAllocatableSpotFallback: zero,
    tpuCostHourlyOnDemand: zero,
    tpuCostHourlySpot: zero,
    tpuCostHourlySpotFallback: zero,
    tpuProvisionedOnDemand: zero,
    tpuProvisionedSpot: zero,
    tpuProvisionedSpotFallback: zero,
    tpuRequestedOnDemand: zero,
    tpuRequestedSpot: zero,
    tpuRequestedSpotFallback: zero,
  };

  return enhanceClusterSummary(normalizedSummary);
};

export const getMonthlySavingsFromOverview = (
  item?: ClusterOverviewItem
): number | null => {
  if (!item) {
    return null;
  }

  const costHourly = toNumOrZero(item.costHourly);
  const optimalCostHourly = toNumOrZero(item.optimalCostHourly);
  const savingsHourly = Math.max(costHourly - optimalCostHourly, 0);

  if (!savingsHourly) {
    return null;
  }

  return savingsHourly * AVERAGE_HOURS_IN_MONTH;
};

export const getWastePercentageFromOverview = (
  item?: ClusterOverviewItem
): number | null => {
  if (!item) {
    return null;
  }

  const costHourly = toNumOrZero(item.costHourly);
  const optimalCostHourly = toNumOrZero(item.optimalCostHourly);

  if (!costHourly) {
    return null;
  }

  return Math.max(((costHourly - optimalCostHourly) / costHourly) * 100, 0);
};

export const deriveEstimatedSavingsMapFromOverview = (
  items: ClusterOverviewItem[] = []
): Map<string, { monthlySpend: number; wastePercentage: number | null }> => {
  const map = new Map<
    string,
    { monthlySpend: number; wastePercentage: number | null }
  >();

  items.forEach((item) => {
    if (!item.clusterId) {
      return;
    }

    const monthlySpend = toNumOrZero(item.costHourly) * AVERAGE_HOURS_IN_MONTH;
    const wastePercentage = getWastePercentageFromOverview(item);

    map.set(item.clusterId, {
      monthlySpend,
      wastePercentage,
    });
  });

  return map;
};

export const deriveSummaryMapFromOverview = (
  items: ClusterOverviewItem[] = []
): Map<
  string,
  { clusterScore: string; nodesCountTotal: { toNumber(): number } }
> => {
  const map = new Map<
    string,
    { clusterScore: string; nodesCountTotal: { toNumber(): number } }
  >();

  items.forEach((item) => {
    if (!item.clusterId) {
      return;
    }

    map.set(item.clusterId, {
      clusterScore: '',
      nodesCountTotal: { toNumber: () => item.nodeCount ?? 0 },
    });
  });

  return map;
};

export const isOverviewClusterDiscovered = (
  item?: ClusterOverviewItem
): boolean => !!item && isDiscoveredOverviewCluster(item);

const average = (values: number[]): number | null =>
  values.length
    ? values.reduce((sum, value) => sum + value, 0) / values.length
    : null;

export const mapOverviewTimeseriesToResourceChart = (
  points: OrganizationOverviewResourceTimeseriesPoint[] = [],
  resourceType: ResourceType
): {
  chartData: ResourceChartPoint[];
  averages: {
    provisioned: number | null;
    used: number | null;
    utilization: number | null;
  };
} => {
  const chartData: ResourceChartPoint[] = orderBy(points, 'timestamp').map(
    (point) => {
      const timestamp = dayjs
        .utc(point.timestamp)
        .subtract(1, 'day')
        .startOf('day');

      if (resourceType === 'storage') {
        const provisioned = toNumOrZero(point.storageProvisionedGib);
        const requested = toNumOrZero(point.storageRequestedGib);
        const used = toNumOrZero(point.storageClaimedGib);
        const utilization = provisioned > 0 ? (used / provisioned) * 100 : 0;

        return {
          timestamp: timestamp.toISOString(),
          chartTimestampUnix: timestamp.unix(),
          provisioned,
          requested,
          requestedGap: Math.max(requested - used, 0),
          used,
          utilization,
        };
      }

      const provisioned =
        resourceType === 'cpu'
          ? toNumOrZero(point.cpuProvisioned)
          : toNumOrZero(point.ramProvisionedGib);
      const requested =
        resourceType === 'cpu'
          ? toNumOrZero(point.cpuRequested)
          : toNumOrZero(point.ramRequestedGib);
      const used =
        resourceType === 'cpu'
          ? toNumOrZero(point.cpuUsed)
          : toNumOrZero(point.ramUsedGib);
      const utilization =
        resourceType === 'cpu'
          ? toNumOrZero(point.cpuUtilization) ||
            (provisioned > 0 ? (used / provisioned) * 100 : 0)
          : toNumOrZero(point.ramUtilization) ||
            (provisioned > 0 ? (used / provisioned) * 100 : 0);

      return {
        timestamp: timestamp.toISOString(),
        chartTimestampUnix: timestamp.unix(),
        provisioned,
        requested,
        requestedGap: Math.max(requested - used, 0),
        used,
        utilization,
      };
    }
  );

  return {
    chartData,
    averages: {
      provisioned: average(chartData.map((point) => point.provisioned)),
      used: average(chartData.map((point) => point.used)),
      utilization: average(chartData.map((point) => point.utilization)),
    },
  };
};

export const mapOverviewTimeseriesToUtilizationSpark = (
  points: OrganizationOverviewResourceTimeseriesPoint[] = [],
  range?: { startTime: string; endTime: string }
): {
  utilizationSparkData: {
    timestamp: string;
    chartTimestampUnix: number;
    cpuUtilization: number;
    memoryUtilization: number;
  }[];
  utilizationSparkDomain: readonly [number, number];
} => {
  const filteredPoints = range
    ? points.filter((point) => {
        if (!point.timestamp) {
          return false;
        }

        const ts = dayjs(point.timestamp);
        return (
          !ts.isBefore(dayjs(range.startTime)) &&
          !ts.isAfter(dayjs(range.endTime))
        );
      })
    : points;

  const utilizationSparkData = orderBy(filteredPoints, 'timestamp').map(
    (point) => {
      const timestamp = dayjs
        .utc(point.timestamp)
        .subtract(1, 'day')
        .startOf('day');
      const cpuProvisioned = toNumOrZero(point.cpuProvisioned);
      const cpuUsed = toNumOrZero(point.cpuUsed);
      const memProvisioned = toNumOrZero(point.ramProvisionedGib);
      const memUsed = toNumOrZero(point.ramUsedGib);

      return {
        timestamp: timestamp.toISOString(),
        chartTimestampUnix: timestamp.unix(),
        cpuUtilization:
          toNumOrZero(point.cpuUtilization) ||
          (cpuProvisioned > 0 ? (cpuUsed / cpuProvisioned) * 100 : 0),
        memoryUtilization:
          toNumOrZero(point.ramUtilization) ||
          (memProvisioned > 0 ? (memUsed / memProvisioned) * 100 : 0),
      };
    }
  );

  const allValues = utilizationSparkData.flatMap((point) => [
    point.cpuUtilization,
    point.memoryUtilization,
  ]);
  const min = allValues.length ? Math.max(0, Math.min(...allValues) - 4) : 0;
  const max = allValues.length
    ? Math.min(100, Math.max(...allValues) + 4)
    : 100;

  return {
    utilizationSparkData,
    utilizationSparkDomain: [min, max] as const,
  };
};

export const mapOverviewToFleetMetrics = (
  overview?: OrganizationOverviewResponse,
  clusters: Cluster[] = []
) => {
  const merged = mergeOverviewSections(overview?.agent, overview?.agentless);
  const summary = merged.summary;

  const providerCounts = new Map<string, number>();
  const providerCostMap = new Map<string, number>();

  clusters.forEach((cluster) => {
    const provider = (cluster.providerType ?? 'unknown').toLowerCase();
    providerCounts.set(provider, (providerCounts.get(provider) ?? 0) + 1);
  });

  summary?.providers?.forEach((entry) => {
    const provider = (entry.provider ?? 'unknown').toLowerCase();
    providerCostMap.set(
      provider,
      toNumOrZero(entry.costHourly) * AVERAGE_HOURS_IN_MONTH
    );
  });

  const connectedTotals = resolveFleetResourceTotals(
    overview?.agent?.summary,
    overview?.agent?.resourceTimeseries?.at(-1)
  );
  const discoveredTotals = resolveFleetResourceTotals(
    overview?.agentless?.summary,
    overview?.agentless?.resourceTimeseries?.at(-1)
  );
  const connectedSummary = overview?.agent?.summary;

  const spotCostHourly = toNumOrZero(summary?.spotCostHourly);
  const onDemandCostHourly = toNumOrZero(summary?.onDemandCostHourly);

  const stats = {
    total: clusters.length,
    online: clusters.filter(
      (cluster) => cluster.agentStatus === AgentStatus.ONLINE
    ).length,
    managed: clusters.filter((cluster) => isClusterManaged(cluster)).length,
    providerCounts,
    providerCostMap,
    totalNodes: summary?.totalNodes ?? 0,
    spotNodes: summary?.spotNodeCount ?? 0,
    onDemandNodes: summary?.onDemandNodeCount ?? 0,
    fallbackNodes: 0,
    spotCostHourly,
    onDemandCostHourly,
    fallbackCostHourly: 0,
    cpuUtilPct:
      toNumOrZero(connectedSummary?.avgCpuUtilization) ||
      (connectedTotals.totalCpuProvisioned > 0
        ? (connectedTotals.totalCpuUsed / connectedTotals.totalCpuProvisioned) *
          100
        : 0),
    memUtilPct:
      toNumOrZero(connectedSummary?.avgRamUtilization) ||
      (connectedTotals.totalRamProvisioned > 0
        ? (connectedTotals.totalRamUsed / connectedTotals.totalRamProvisioned) *
          100
        : 0),
  };

  const nodeCosts = {
    spotMonthlyCost: spotCostHourly * AVERAGE_HOURS_IN_MONTH,
    onDemandMonthlyCost: onDemandCostHourly * AVERAGE_HOURS_IN_MONTH,
    fallbackMonthlyCost: 0,
  };

  const resources = {
    cpu: {
      connected: connectedTotals.totalCpuProvisioned,
      discovered: discoveredTotals.totalCpuProvisioned,
      used: connectedTotals.totalCpuUsed,
      unit: 'vCPU' as const,
    },
    memory: buildFleetMemoryMetrics({
      connectedGiB: connectedTotals.totalRamProvisioned,
      discoveredGiB: discoveredTotals.totalRamProvisioned,
      usedGiB: connectedTotals.totalRamUsed,
    }),
    gpu: {
      connected: connectedTotals.totalGpuProvisioned,
      discovered: discoveredTotals.totalGpuProvisioned,
      used: connectedTotals.totalGpuUsed,
      unit: 'GPUs' as const,
    },
  };

  return { stats, resources, nodeCosts, merged };
};
